import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SchoolTypes, SchoolTypeConfig } from '../../interfaces/school.interface';
import { IUser } from '../../interfaces/user.interface';
import { ActiveUser } from '../../utils/helpers.utils';

@Injectable({
  providedIn: 'root'
})
export class SchoolContextService {
  private currentSchoolType = new BehaviorSubject<SchoolTypes>(SchoolTypes.SECONDARY);
  private schoolConfig = new BehaviorSubject<SchoolTypeConfig | null>(null);

  // School type configurations
  private readonly schoolTypeConfigs: Map<SchoolTypes, SchoolTypeConfig> = new Map([
    [SchoolTypes.PRIMARY, {
      type: SchoolTypes.PRIMARY,
      labels: {
        subject: 'Subject',
        student: 'Pupil',
        evaluation: 'Assessment',
        term: 'Term',
        courseRegistration: 'Subject Registration',
        studentRegistration: 'Pupil Registration',
        academicLevel: 'Class',
        sequence: 'Sequence',
        semester: 'Term',
        department: 'Section',
        specialization: 'Focus Area'
      },
      features: {
        requiresCourseRegistration: false,
        requiresStudentRegistration: false,
        usesPromotion: true,
        usesSemesters: false,
        usesTerms: true,
        usesSequences: true,
        usesClassrooms: true
      }
    }],
    [SchoolTypes.SECONDARY, {
      type: SchoolTypes.SECONDARY,
      labels: {
        subject: 'Subject',
        student: 'Student',
        evaluation: 'Evaluation',
        term: 'Term',
        courseRegistration: 'Subject Registration',
        studentRegistration: 'Student Registration',
        academicLevel: 'Cycles',
        sequence: 'Sequence',
        semester: 'Term',
        department: 'Department',
        specialization: 'Specialization'
      },
      features: {
        requiresCourseRegistration: false,
        requiresStudentRegistration: false,
        usesPromotion: true,
        usesSemesters: false,
        usesTerms: true,
        usesSequences: true,
        usesClassrooms: true
      }
    }],
    [SchoolTypes.HIGHER, {
      type: SchoolTypes.HIGHER,
      labels: {
        subject: 'Course',
        student: 'Student',
        evaluation: 'Continuous Assessment (CA)',
        term: 'Semester',
        courseRegistration: 'Course Registration',
        studentRegistration: 'Student Registration',
        academicLevel: 'Level',
        sequence: 'Sequence',
        semester: 'Semester',
        department: 'Department',
        specialization: 'Faculties'
      },
      features: {
        requiresCourseRegistration: true,
        requiresStudentRegistration: true,
        usesPromotion: false,
        usesSemesters: true,
        usesTerms: false,
        usesSequences: true,
        usesClassrooms: false
      }
    }],
    [SchoolTypes.UNIVERSITY, {
      type: SchoolTypes.UNIVERSITY,
      labels: {
        subject: 'Course',
        student: 'Student',
        evaluation: 'Continuous Assessment (CA)',
        term: 'Semester',
        courseRegistration: 'Course Registration',
        studentRegistration: 'Student Registration',
        academicLevel: 'Level',
        sequence: 'Sequence',
        semester: 'Semester',
        department: 'Department',
        specialization: 'Faculties'
      },
      features: {
        requiresCourseRegistration: true,
        requiresStudentRegistration: true,
        usesPromotion: false,
        usesSemesters: true,
        usesTerms: false,
        usesSequences: true,
        usesClassrooms: false
      }
    }]
  ]);

  constructor() {
    this.initializeSchoolType();
  }

  /**
   * Initialize the school type from the current user
   */
  private initializeSchoolType(): void {
    try {
      const user: IUser = ActiveUser();
      if (user?.school?.type) {
        this.setSchoolType(user.school.type);
      }
    } catch (error) {
      console.warn('Could not initialize school type from user context:', error);
      // Default to secondary school
      this.setSchoolType(SchoolTypes.SECONDARY);
    }
  }

  /**
   * Set the current school type
   */
  setSchoolType(schoolType: SchoolTypes): void {
    this.currentSchoolType.next(schoolType);
    const config = this.schoolTypeConfigs.get(schoolType);
    this.schoolConfig.next(config || null);
  }

  /**
   * Get the current school type
   */
  getCurrentSchoolType(): Observable<SchoolTypes> {
    return this.currentSchoolType.asObservable();
  }

  /**
   * Get the current school type synchronously
   */
  getCurrentSchoolTypeSync(): SchoolTypes {
    return this.currentSchoolType.value;
  }

  /**
   * Get the current school configuration
   */
  getCurrentSchoolConfig(): Observable<SchoolTypeConfig | null> {
    return this.schoolConfig.asObservable();
  }

  /**
   * Get the current school configuration synchronously
   */
  getCurrentSchoolConfigSync(): SchoolTypeConfig | null {
    return this.schoolConfig.value;
  }

  /**
   * Get a specific label for the current school type
   */
  getLabel(key: keyof SchoolTypeConfig['labels']): string {
    const config = this.schoolConfig.value;
    return config?.labels[key] || key;
  }

  /**
   * Get a specific feature flag for the current school type
   */
  getFeature(key: keyof SchoolTypeConfig['features']): Observable<boolean> {
    const config = this.schoolConfig.value;
    return new Observable(observer => {
      observer.next(config?.features[key] || false);
      observer.complete();
    });
  }

  /**
   * Get a specific feature flag for the current school type synchronously
   */
  getFeatureSync(key: keyof SchoolTypeConfig['features']): boolean {
    const config = this.schoolConfig.value;
    return config?.features[key] || false;
  }

  /**
   * Check if the current school is a university
   */
  isUniversity(): boolean {
    return this.currentSchoolType.value === SchoolTypes.UNIVERSITY || 
           this.currentSchoolType.value === SchoolTypes.HIGHER;
  }

  /**
   * Check if the current school is a secondary school
   */
  isSecondarySchool(): boolean {
    return this.currentSchoolType.value === SchoolTypes.SECONDARY;
  }

  /**
   * Check if the current school is a primary school
   */
  isPrimarySchool(): boolean {
    return this.currentSchoolType.value === SchoolTypes.PRIMARY;
  }

  /**
   * Update school type from user context (called after login)
   */
  updateFromUser(user: IUser): void {
    if (user?.school?.type) {
      this.setSchoolType(user.school.type);
    }
  }

  /**
   * Get all available school types
   */
  getAvailableSchoolTypes(): SchoolTypes[] {
    return Array.from(this.schoolTypeConfigs.keys());
  }

  /**
   * Get configuration for a specific school type
   */
  getSchoolTypeConfig(schoolType: SchoolTypes): SchoolTypeConfig | undefined {
    return this.schoolTypeConfigs.get(schoolType);
  }
}
